// src/context/NotificationContext.js - CORREGIDO
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useSocket } from './SocketContext';
import notificationService from '../services/notificationService';
import { useNavigate } from 'react-router-dom';
import {
  addSystemNotificationActionListener,
  clearDeliveredSystemNotifications,
  removeDeliveredSystemNotification,
  requestSystemNotificationPermission,
  showSystemNotification,
  usesNativeSystemNotifications
} from '../services/nativeNotificationService';
import { registerNativePushNotifications } from '../services/nativePushNotificationService';


const NotificationContext = createContext();


export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationProvider');
  }
  return context;
};


export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const socket = useSocket();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);


  // Cargar notificaciones iniciales
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await notificationService.getNotifications(token);
      // Manejar el formato { success: true, notifications: [...] }
      const notificationsList = data.success ? data.notifications : data;
      setNotifications(notificationsList || []);
    } catch (err) {
      console.error('Error al cargar notificaciones:', err);
      setError('No se pudieron cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Solicitar permisos
  const requestNotificationPermission = useCallback(async () => {
    try {
      await requestSystemNotificationPermission();
    } catch (permissionError) {
      console.warn('Notification permission could not be requested:', permissionError);
    }
  }, []);


  // Suscribirse a eventos de notificaciones del socket
  useEffect(() => {
    if (!socket || !user) return;


    const handleNewNotification = (notification) => {
      console.log('📬 Nueva notificación recibida:', notification);
      setNotifications((prev) => [notification, ...prev]);
      
      if (!usesNativeSystemNotifications()) playNotificationSound();
      showSystemNotification(notification).catch((notificationError) => {
        console.warn('System notification could not be displayed:', notificationError);
      });
    };


    const handleNotificationUpdate = (data) => {
      console.log('🔄 Notificación actualizada:', data);
      setNotifications((prev) =>
        prev.map((n) => (n.id === data.id ? { ...n, ...data } : n))
      );
    };


    const handleNotificationDeleted = (notificationId) => {
      console.log('🗑️ Notificación eliminada:', notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    };


    socket.on('notification:new', handleNewNotification);
    socket.on('notification:update', handleNotificationUpdate);
    socket.on('notification:deleted', handleNotificationDeleted);


    return () => {
      socket.off('notification:new', handleNewNotification);
      socket.off('notification:update', handleNotificationUpdate);
      socket.off('notification:deleted', handleNotificationDeleted);
    };
  }, [socket, user]);


  // Cargar notificaciones al montar
  useEffect(() => {
    if (user && token) {
      fetchNotifications();
      if (!usesNativeSystemNotifications()) requestNotificationPermission();
    }
  }, [user, token, fetchNotifications, requestNotificationPermission]);


  // Marcar como leída
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId, token);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      await removeDeliveredSystemNotification(notificationId).catch(() => {});
      return true;
    } catch (err) {
      console.error('Unable to mark notification as read:', err);
      setError('The notification could not be marked as read');
      return false;
      // ✅ NO hacer throw para evitar errores no controlados
    }
  }, [token]);


  // Marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead(token);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      await clearDeliveredSystemNotifications().catch(() => {});
      return true;
    } catch (err) {
      console.error('Unable to mark all notifications as read:', err);
      setError('Notifications could not be marked as read');
      return false;
      // ✅ NO hacer throw para evitar errores no controlados
    }
  }, [token]);


  // Eliminar notificación
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId, token);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      await removeDeliveredSystemNotification(notificationId).catch(() => {});
      return true;
    } catch (err) {
      console.error('Unable to delete notification:', err);
      setError('The notification could not be deleted');
      return false;
      // ✅ NO hacer throw para evitar errores no controlados
    }
  }, [token]);


  // ✅ CORREGIDO: Eliminar todas sin throw err
  const clearAll = useCallback(async () => {
    try {
      await notificationService.clearAll(token);
      setNotifications([]);
      await clearDeliveredSystemNotifications().catch(() => {});
    } catch (err) {
      console.error('Error al limpiar notificaciones:', err);
      setError('No se pudieron limpiar las notificaciones');
      // ✅ NO hacer throw para evitar el "Uncaught runtime error"
    }
  }, [token]);

  useEffect(() => {
    if (!user) return undefined;

    let listenerHandle;
    let active = true;
    addSystemNotificationActionListener(async ({ notificationId, route }) => {
      if (!active) return;
      if (notificationId) await markAsRead(notificationId);
      const destination = String(route || '/notifications');
      navigate(destination.startsWith('/') ? destination : '/notifications');
    }).then((handle) => {
      listenerHandle = handle;
    }).catch((listenerError) => {
      console.warn('Notification action listener could not be registered:', listenerError);
    });

    return () => {
      active = false;
      listenerHandle?.remove?.();
    };
  }, [markAsRead, navigate, user]);

  useEffect(() => {
    if (!user || !token || !usesNativeSystemNotifications()) return undefined;

    let active = true;
    let pushHandle;

    requestSystemNotificationPermission()
      .then(() => registerNativePushNotifications({
        authToken: token,
        onReceived: () => {
          if (active) fetchNotifications();
        },
        onAction: async ({ notificationId, route }) => {
          if (!active) return;
          if (notificationId) await markAsRead(notificationId);
          const destination = String(route || '/notifications');
          navigate(destination.startsWith('/') ? destination : '/notifications');
        }
      }))
      .then((handle) => {
        pushHandle = handle;
      }).catch((pushError) => {
        console.warn('Push notifications could not be initialized:', pushError);
      });

    return () => {
      active = false;
      pushHandle?.remove?.();
    };
  }, [fetchNotifications, markAsRead, navigate, token, user]);


  // Reproducir sonido
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/sounds/notification.mp3');
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (err) {
      console.error('Error al reproducir sonido:', err);
    }
  };


  const unreadCount = notifications.filter((n) => !n.read).length;


  const value = {
    notifications,
    loading,
    error,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };


  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
